import { useContext } from "@nuxtjs/composition-api";

export function useTenderEnums() {
  const { i18n } = useContext();
  const TEXT_CHANNEL_STATUS = {
    NO_TEXT_CHANNEL: "no_text_channel",
    WAITING_FOR_APPROVAL: "waiting_for_approval",
    APPROVED_TENDER: "approved_tender",
    APPROVED_NO_TENDER: "approved_no_tender",
    DISABLED: "disabled",
  };

  const textChannelStatusOptions = Object.values(TEXT_CHANNEL_STATUS).map(
    (value) => ({
      id: value,
      name: i18n.t(`conductor.tenders.textChannelStatuses.${value}`),
    })
  );

  const STATUS = {
    CLOSED: "closed",
    OPEN: "open",
  };

  const statusOptions = Object.values(STATUS).map((value) => ({
    id: value,
    name: i18n.t(`conductor.tenders.statuses.${value}`),
  }));

  const SOURCE = {
    TEXT: "text",
    PHONE: "phone",
    BOX: "box",
    HOTLINE: "hotline",
    MOBILE_APP: "mobile_app",
    NOT_AVAILABLE: "not_available",
  };

  const sourceOptions = Object.values(SOURCE).map((value) => ({
    id: value,
    name: i18n.t(`conductor.tenders.sources.${value}`),
  }));

  const CLOSE_REASON = {
    SPAM: "spam",
    JOB_OFFER: "job_offer",
    DUPLICATION: "duplication",
    WRONG_NUMBER: "wrong_number",
    WRONG_CITY: "wrong_city",
    WRONG_PROFESSIONAL: "wrong_professional",
    WANTS_TO_REGISTER: "wants_to_register",
    NEEDS_MORE_DETAILS: "needs_more_details",
    PROFESSIONAL_NOT_AVAILABLE: "professional_not_available",
    DELAYED_EXECUTION: "delayed_execution",
    JUST_CURIOUS: "just_curious",
    NOT_SERIOUS: "not_serious",
    NO_ANSWER_5: "no_answer_5",
    OTHER: "other",
  };

  const JUDGE_STANDARD_CLOSE_REASONS = [
    CLOSE_REASON.SPAM,
    CLOSE_REASON.JOB_OFFER,
    CLOSE_REASON.DUPLICATION,
    CLOSE_REASON.WRONG_NUMBER,
    CLOSE_REASON.WRONG_CITY,
    CLOSE_REASON.WRONG_PROFESSIONAL,
    CLOSE_REASON.WANTS_TO_REGISTER,
    CLOSE_REASON.NEEDS_MORE_DETAILS,
    CLOSE_REASON.PROFESSIONAL_NOT_AVAILABLE,
    CLOSE_REASON.OTHER,
  ];

  const JUDGE_VALIDATION_CLOSE_REASONS = [
    CLOSE_REASON.DELAYED_EXECUTION,
    CLOSE_REASON.JUST_CURIOUS,
    CLOSE_REASON.NOT_SERIOUS,
    CLOSE_REASON.WRONG_NUMBER,
    CLOSE_REASON.NO_ANSWER_5,
    CLOSE_REASON.DUPLICATION,
    CLOSE_REASON.OTHER,
  ];

  const JUDGE_VALIDATION_EMAIL_CLOSE_REASONS = [CLOSE_REASON.WRONG_NUMBER];

  const closeReasonOption = (value) => ({
    id: value,
    name: i18n.t(`conductor.tenders.closeReasons.${value}`),
  });

  const judgeStandardCloseReasonOptions =
    JUDGE_STANDARD_CLOSE_REASONS.map(closeReasonOption);

  const judgeValidationCloseReasonOptions =
    JUDGE_VALIDATION_CLOSE_REASONS.map(closeReasonOption);

  const judgeCloseReasonOptionsFor = (validate) =>
    validate
      ? judgeValidationCloseReasonOptions
      : judgeStandardCloseReasonOptions;

  const JUDGE_WAIT_OPTION = {
    NO_ANSWER: "no_answer",
    WITHOUT_CALL: "without_call",
  };

  const judgeWaitOptions = [
    JUDGE_WAIT_OPTION.NO_ANSWER,
    JUDGE_WAIT_OPTION.WITHOUT_CALL,
  ].map((value) => ({
    id: value,
    name: i18n.t(`conductor.tenders.judge.waitOptions.${value}`),
  }));

  const VALIDATION_STATUS = {
    NOT_VALIDATED: "not_validated",
    NOT_VALID: "not_valid",
    VALID: "valid",
    VALID_WANTS_SURVEY: "valid_wants_survey",
  };

  const JUDGE_VALIDATION_STATUS_FILTER = [
    VALIDATION_STATUS.NOT_VALIDATED,
    VALIDATION_STATUS.NOT_VALID,
    VALIDATION_STATUS.VALID,
    VALIDATION_STATUS.VALID_WANTS_SURVEY,
  ];

  const validationStatusOptions = JUDGE_VALIDATION_STATUS_FILTER.map(
    (value) => ({
      id: value,
      name: i18n.t(`conductor.tenders.validationStatuses.${value}`),
    })
  );

  const VALIDATION_STATUS_PILL_TYPE = {
    [VALIDATION_STATUS.NOT_VALID]: "danger",
    [VALIDATION_STATUS.VALID]: "success",
    [VALIDATION_STATUS.VALID_WANTS_SURVEY]: "info",
  };

  const DEADLINE = {
    FEW_DAYS: "few_days",
    ONE_WEEK: "one_week",
    TWO_WEEKS: "two_weeks",
    ONE_MONTH: "one_month",
    TWO_MONTHS: "two_months",
    FLEXIBLE: "flexible",
    CURIOUS: "curious",
  };

  const deadlineOptions = Object.values(DEADLINE).map((value) => ({
    value,
    label: i18n.t(`conductor.tenders.deadline.${value}`),
  }));

  const EXTENT_OF_WORK = {
    SMALL: "small",
    BIG: "big",
    EXTRA_BIG: "extra_big",
    DONT_KNOW: "dont_know",
  };

  const extentOfWorkOptions = Object.values(EXTENT_OF_WORK).map((value) => ({
    value,
    label: i18n.t(`conductor.tenders.extentOfWork.${value}`),
  }));

  const MARKET_FILTER = {
    ALL: "all",
    TOP_RATED: "top_rated",
    EXPERIENCED: "experienced",
  };

  const MARKET_FILTER_VALUES = {
    all: null,
    top_rated: [2],
    experienced: [4],
  };

  const marketFilterOptions = Object.keys(MARKET_FILTER_VALUES).map(
    (value) => ({
      value,
      label: i18n.t(`conductor.tenders.marketFilter.${value}`),
    })
  );

  const marketFilterToArray = (key) => MARKET_FILTER_VALUES[key] ?? null;

  // Shortcuts for the utm_source values we run campaigns on; the pickers using
  // this also accept any other source as free text.
  const CAMPAIGN_SOURCES = ["google", "facebook"];

  const campaignSourceOptions = CAMPAIGN_SOURCES.map((value) => ({
    id: value,
    name: value,
  }));

  return {
    TEXT_CHANNEL_STATUS,
    textChannelStatusOptions,
    STATUS,
    statusOptions,
    SOURCE,
    sourceOptions,
    CLOSE_REASON,
    JUDGE_STANDARD_CLOSE_REASONS,
    JUDGE_VALIDATION_CLOSE_REASONS,
    JUDGE_VALIDATION_EMAIL_CLOSE_REASONS,
    judgeStandardCloseReasonOptions,
    judgeValidationCloseReasonOptions,
    judgeCloseReasonOptionsFor,
    JUDGE_WAIT_OPTION,
    judgeWaitOptions,
    VALIDATION_STATUS,
    JUDGE_VALIDATION_STATUS_FILTER,
    validationStatusOptions,
    VALIDATION_STATUS_PILL_TYPE,
    DEADLINE,
    deadlineOptions,
    EXTENT_OF_WORK,
    extentOfWorkOptions,
    MARKET_FILTER,
    marketFilterOptions,
    marketFilterToArray,
    CAMPAIGN_SOURCES,
    campaignSourceOptions,
  };
}
